// UC Hospitality — placeholder tile for the upcoming Phonesuite partnership app.
//
// Scope (per Keith, Feb 2026): Leads, CRM, Sales, Billing, 50/50 profit
// share & expense tracking, Trunking, 911, integrated Phonesuite provisioning.
// Julian owns MVP after Conduit 2 ships. Framework instructions to follow.
// Tile is seeded with status="inactive" so it stays off the dashboard/catalog.
//
// Iter 91w — added three collaborator-facing sections while the app is still
// dormant, driven by /api/hospitality/*:
//   1. FrameworkNotes  — RichTextEditor for super_admins to paste Keith's
//                        Phonesuite integration notes when they arrive.
//   2. LedgerPreview   — mock 50/50 profit-share breakdown card so stakeholders
//                        see the vision without any real data.
//   3. SchemaPreview   — collapsible sketch of the planned Mongo collections
//                        so Julian can start MVP right after Conduit 2 ships.

import { useEffect, useMemo, useState } from "react";
import { Hotel, Sparkles, PencilLine, FileText, Database, DollarSign, Save, RotateCcw, FileDown, Plus, UserPlus, LayoutGrid, List, Phone, Mail, StickyNote, CalendarClock, ArrowRight, MoveRight, Building2, Upload, FileSpreadsheet, TrendingUp, Paperclip, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import DOMPurify from "dompurify";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import api, { formatApiErrorDetail, API as API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PLANNED = [
  "Leads & CRM pipeline scoped to Phonesuite properties",
  "Sales workflow — quotes, orders, contract lifecycle",
  "Billing + 50/50 profit share tracking with expense ledger",
  "Trunking provisioning & health telemetry",
  "E911 registration, address validation and audit trail",
  "Deep Phonesuite PBX integration (config sync, provisioning, CDR)",
];

const CAD = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function FrameworkNotes({ isSuperAdmin }) {
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_by_name: "", updated_at: "" });
  // Iter 91ac — Phonesuite partner logo (for the PDF cover page)
  const [partnerLogo, setPartnerLogo] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    api.get("/hospitality/settings")
      .then(({ data }) => {
        setNotes(data.framework_notes || "");
        setDraft(data.framework_notes || "");
        setPartnerLogo(data.partner_logo_url || "");
        setMeta({ updated_by_name: data.updated_by_name || "", updated_at: data.updated_at || "" });
      })
      .catch(() => {});
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setUploadingLogo(true);
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      // Persist directly as a data URL — the PDF builder decodes data URLs
      // via `_qbr_logo` so no separate upload step is required.
      const { data: fresh } = await api.patch("/hospitality/settings", { partner_logo_url: data });
      setPartnerLogo(fresh.partner_logo_url || data);
      toast.success("Phonesuite logo saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const clearLogo = async () => {
    setUploadingLogo(true);
    try {
      // send empty string to clear
      await api.patch("/hospitality/settings", { partner_logo_url: "" });
      setPartnerLogo("");
      toast.success("Phonesuite logo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/hospitality/settings", { framework_notes: draft });
      setNotes(data.framework_notes || "");
      setMeta({ updated_by_name: data.updated_by_name || "", updated_at: data.updated_at || "" });
      setEditing(false);
      toast.success("Framework notes saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const hasNotes = notes.trim().length > 0;

  return (
    <Card className="p-6" data-testid="uc-hospitality-framework-notes">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <FileText className="h-4 w-4 text-primary" /> Framework Notes
          <span className="text-xs font-normal text-slate-400">— drop-zone for Keith&apos;s Phonesuite integration write-up</span>
        </div>
        {isSuperAdmin && !editing && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" data-testid="uc-hospitality-notes-pdf"
                    onClick={() => window.open(`${API_BASE}/hospitality/framework.pdf`, "_blank")}>
              <FileDown className="h-3.5 w-3.5" /> Export PDF
            </Button>
            <Button size="sm" variant="outline" data-testid="uc-hospitality-notes-edit"
                    onClick={() => { setDraft(notes); setEditing(true); }}>
              <PencilLine className="h-3.5 w-3.5" /> {hasNotes ? "Edit" : "Add notes"}
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <RichTextEditor value={draft} onChange={setDraft} testid="uc-hospitality-notes-editor"
                          placeholder="Paste Keith's Phonesuite framework, integration flow, endpoints, sequence diagrams…" />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(notes); }} disabled={saving}
                    data-testid="uc-hospitality-notes-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="uc-hospitality-notes-save">
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : hasNotes ? (
        <>
          <div className="prose prose-sm max-w-none text-sm text-slate-700 [&_a]:text-primary [&_a]:underline [&_img]:rounded-lg [&_img]:max-w-full [&_img]:h-auto"
               data-testid="uc-hospitality-notes-body"
               dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notes) }} />
          {meta.updated_by_name && (
            <div className="mt-3 text-[11px] text-slate-400" data-testid="uc-hospitality-notes-meta">
              Last updated by {meta.updated_by_name}{meta.updated_at ? ` · ${new Date(meta.updated_at).toLocaleString()}` : ""}
            </div>
          )}
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
             data-testid="uc-hospitality-notes-empty">
          Awaiting Keith&apos;s framework notes. A super admin can paste them here as soon as they land — no code push required.
        </div>
      )}

      {/* Iter 91ac — Phonesuite partner logo → surfaces on the PDF cover. */}
      {isSuperAdmin && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/50 p-3"
             data-testid="uc-hospitality-partner-logo">
          <div className="flex h-12 w-24 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white">
            {partnerLogo ? (
              <img src={partnerLogo} alt="Phonesuite logo"
                   data-testid="uc-hospitality-partner-logo-img"
                   className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">No logo</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-slate-700">Phonesuite logo (PDF cover)</div>
            <div className="text-[11px] text-slate-500">
              PNG or JPG, ≤ 2 MB. Rendered top-right of the framework PDF; the text placeholder is used if empty.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className={`inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium ${uploadingLogo ? "cursor-wait opacity-60" : "cursor-pointer hover:border-primary hover:text-primary"}`}
                   data-testid="uc-hospitality-partner-logo-upload">
              <FileDown className="h-3.5 w-3.5 rotate-180" /> {partnerLogo ? "Replace" : "Upload"}
              <input type="file" accept="image/png,image/jpeg" className="hidden" disabled={uploadingLogo}
                     onChange={(e) => uploadLogo(e.target.files?.[0])} />
            </label>
            {partnerLogo && (
              <Button size="sm" variant="ghost" onClick={clearLogo} disabled={uploadingLogo}
                      data-testid="uc-hospitality-partner-logo-clear">Remove</Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function LedgerPreview({ isSuperAdmin }) {
  const [data, setData] = useState(null);
  // ucPct is the LIVE preview value (client-side, no persistence until Save).
  const [ucPct, setUcPct] = useState(null);
  // savedPct is the last server-persisted default — used to detect dirty state
  // and to snap back on Reset.
  const [savedPct, setSavedPct] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hospitality/ledger-preview").then(({ data }) => {
      setData(data);
      setUcPct(data.ratio.uc_pct);
      setSavedPct(data.ratio.uc_pct);
    }).catch(() => setData(null));
  }, []);

  // Recalculate totals + per-row shares in-place off the raw revenue/expenses
  // so slider drags feel instant (no round-trip).
  const view = useMemo(() => {
    if (!data || ucPct == null) return null;
    const uc = ucPct;
    const ps = Math.round((100 - uc) * 100) / 100;
    let tRev = 0, tExp = 0, tNet = 0, tUc = 0, tPs = 0;
    const rows = data.rows.map((r) => {
      const net = r.revenue - r.expenses;
      const ucS = Math.round(net * uc) / 100;
      const psS = Math.round(net * ps) / 100;
      tRev += r.revenue; tExp += r.expenses; tNet += net;
      tUc  += ucS;       tPs  += psS;
      return { ...r, net, uc_share: ucS, phonesuite_share: psS };
    });
    return {
      uc, ps, rows,
      totals: {
        revenue: tRev, expenses: tExp, net: tNet,
        uc_share: Math.round(tUc * 100) / 100,
        phonesuite_share: Math.round(tPs * 100) / 100,
      },
    };
  }, [data, ucPct]);

  if (!view) return null;

  const dirty = savedPct != null && ucPct !== savedPct;

  const applyPreset = (n) => setUcPct(n);

  const saveDefault = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.patch("/hospitality/settings", { uc_share_pct: ucPct });
      setSavedPct(d.uc_share_pct);
      toast.success(`Default split saved — UC ${d.uc_share_pct}% / Phonesuite ${d.phonesuite_share_pct}%`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setUcPct(savedPct);

  return (
    <Card className="p-6" data-testid="uc-hospitality-ledger-preview">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <DollarSign className="h-4 w-4 text-primary" /> Profit-Share Ledger Preview
          {dirty && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
                  data-testid="uc-hospitality-ledger-dirty">
              Unsaved preview
            </span>
          )}
        </div>
        <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500" data-testid="uc-hospitality-ledger-mock-badge">
          Mock data · illustrative
        </Badge>
      </div>

      {/* Live ratio slider — Keith can drag to preview 60/40, 70/30, etc. */}
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4"
           data-testid="uc-hospitality-ratio-panel">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Profit-share split</div>
          <div className="tabular-nums text-sm">
            <span className="font-semibold text-emerald-600" data-testid="uc-hospitality-ratio-uc">UC {view.uc}%</span>
            <span className="mx-2 text-slate-300">/</span>
            <span className="font-semibold text-blue-600" data-testid="uc-hospitality-ratio-ps">Phonesuite {view.ps}%</span>
          </div>
        </div>
        <Slider min={0} max={100} step={1} value={[ucPct]} onValueChange={(v) => setUcPct(v[0])}
                data-testid="uc-hospitality-ratio-slider" />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-slate-500">Presets:</span>
            {[50, 55, 60, 65, 70, 75].map((n) => (
              <button key={n} type="button" onClick={() => applyPreset(n)}
                      data-testid={`uc-hospitality-ratio-preset-${n}`}
                      className={`rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${
                        ucPct === n
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-slate-200 bg-white text-slate-600 hover:border-primary/40 hover:text-primary"
                      }`}>
                {n}/{100 - n}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <Button size="sm" variant="ghost" onClick={reset} data-testid="uc-hospitality-ratio-reset">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
            {isSuperAdmin && (
              <Button size="sm" onClick={saveDefault} disabled={!dirty || saving}
                      data-testid="uc-hospitality-ratio-save">
                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save as default"}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { label: "Revenue",         value: view.totals.revenue,           tone: "text-slate-800" },
          { label: "Expenses",        value: view.totals.expenses,          tone: "text-rose-600"  },
          { label: "Net",             value: view.totals.net,               tone: "text-slate-800" },
          { label: `UC share (${view.uc}%)`,               value: view.totals.uc_share,         tone: "text-emerald-600" },
          { label: `Phonesuite share (${view.ps}%)`, value: view.totals.phonesuite_share, tone: "text-blue-600"    },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{k.label}</div>
            <div className={`mt-0.5 text-base font-semibold ${k.tone}`}>{CAD(k.value)}</div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm" data-testid="uc-hospitality-ledger-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Property</th>
              <th className="px-3 py-2 text-right font-medium">Revenue</th>
              <th className="px-3 py-2 text-right font-medium">Expenses</th>
              <th className="px-3 py-2 text-right font-medium">Net</th>
              <th className="px-3 py-2 text-right font-medium">UC</th>
              <th className="px-3 py-2 text-right font-medium">Phonesuite</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {view.rows.map((r) => (
              <tr key={r.property} className="text-slate-700">
                <td className="px-3 py-2">{r.property}</td>
                <td className="px-3 py-2 text-right tabular-nums">{CAD(r.revenue)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-600">{CAD(r.expenses)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{CAD(r.net)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{CAD(r.uc_share)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-blue-600">{CAD(r.phonesuite_share)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-400">
        Drag the slider to preview any split — numbers recalc live from the sample deals. Super admin can persist the default via <span className="font-medium">Save as default</span>.
      </div>
    </Card>
  );
}

function SchemaPreview() {
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  useEffect(() => {
    api.get("/hospitality/schema").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  return (
    <Card className="p-6" data-testid="uc-hospitality-schema-preview">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Database className="h-4 w-4 text-primary" /> Draft Data Model
        <span className="text-xs font-normal text-slate-400">— what Julian will wire post-Conduit-2</span>
      </div>
      <div className="space-y-3" data-testid="uc-hospitality-schema-list">
        {data.collections.map((c) => {
          const fields = Object.entries(c.schema.properties || {});
          const open = openKey === c.name;
          return (
            <div key={c.name} className="rounded-lg border border-slate-200 bg-white">
              <button type="button" onClick={() => setOpenKey(open ? null : c.name)}
                      data-testid={`uc-hospitality-schema-toggle-${c.name}`}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{c.name}</div>
                  <div className="text-xs text-slate-500">{fields.length} fields · click to {open ? "hide" : "peek"}</div>
                </div>
                <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>
              </button>
              {open && (
                <div className="grid gap-3 border-t border-slate-100 px-3 py-2 md:grid-cols-2">
                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Fields</div>
                    <ul className="space-y-0.5 text-xs">
                      {fields.map(([name, spec]) => (
                        <li key={name} className="flex items-baseline gap-2">
                          <span className="font-mono text-slate-700">{name}</span>
                          <span className="text-slate-400">{spec.type || spec.anyOf?.map((a) => a.type).join(" | ") || "any"}</span>
                          {spec.enum && <span className="text-[10px] text-slate-400">({spec.enum.join(" · ")})</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Sample row</div>
                    <pre className="max-h-64 overflow-auto rounded bg-slate-900 px-2 py-2 text-[10.5px] leading-relaxed text-emerald-200">
{JSON.stringify(c.example, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const STAGES = [
  { value: "new",         label: "New",         tone: "bg-slate-100 text-slate-700" },
  { value: "qualified",   label: "Qualified",   tone: "bg-blue-100 text-blue-700"   },
  { value: "quote_sent",  label: "Quote sent",  tone: "bg-amber-100 text-amber-800" },
  { value: "won",         label: "Won",         tone: "bg-emerald-100 text-emerald-700" },
  { value: "lost",        label: "Lost",        tone: "bg-rose-100 text-rose-700"   },
];
const stageBadge = (s) => STAGES.find((x) => x.value === s) || STAGES[0];

const EMPTY_LEAD = {
  property_name: "", brand: "", room_count: "", contact_name: "", contact_email: "",
  contact_phone: "", city: "", province_state: "", country: "CA", stage: "new",
  source: "", phonesuite_pbx_model: "", notes: "",
};

function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_LEAD);
  // Iter 91aa — view mode toggle + drawer
  const [view, setView] = useState(() => localStorage.getItem("uc_hospitality_leads_view") || "table");
  const [drawerId, setDrawerId] = useState(null);
  // Iter 91ad — CSV bulk import
  const [importOpen, setImportOpen] = useState(false);
  const setViewPersist = (v) => { setView(v); localStorage.setItem("uc_hospitality_leads_view", v); };

  const load = () => {
    setLoading(true);
    api.get("/hospitality/leads")
      .then(({ data }) => setLeads(data.leads || []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Drag-and-drop between Kanban columns — PATCH the lead's stage on drop.
  const moveStage = async (leadId, newStage) => {
    const lead = leads.find((l) => l.lead_id === leadId);
    if (!lead || lead.stage === newStage) return;
    // optimistic
    setLeads((rows) => rows.map((r) => (r.lead_id === leadId ? { ...r, stage: newStage } : r)));
    try {
      await api.patch(`/hospitality/leads/${leadId}`, { stage: newStage });
      toast.success(`${lead.property_name} → ${stageBadge(newStage).label}`);
    } catch (err) {
      // rollback
      setLeads((rows) => rows.map((r) => (r.lead_id === leadId ? { ...r, stage: lead.stage } : r)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed");
    }
  };

  const submit = async () => {
    if (!form.property_name.trim() || !form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error("Property name, contact name and email are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.room_count === "" || payload.room_count == null) delete payload.room_count;
      else payload.room_count = parseInt(payload.room_count, 10);
      if (payload.est_mrr_cad === "" || payload.est_mrr_cad == null) delete payload.est_mrr_cad;
      else payload.est_mrr_cad = parseFloat(payload.est_mrr_cad);
      // strip blanks so backend keeps them nullable
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      // property_name / contact_name / contact_email are required — restore them
      payload.property_name = form.property_name.trim();
      payload.contact_name = form.contact_name.trim();
      payload.contact_email = form.contact_email.trim();
      payload.stage = form.stage || "new";
      payload.country = form.country || "CA";
      await api.post("/hospitality/leads", payload);
      toast.success(`Lead captured — ${form.property_name}`);
      setOpen(false);
      setForm(EMPTY_LEAD);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6" data-testid="uc-hospitality-leads-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <UserPlus className="h-4 w-4 text-primary" /> Prospect Pipeline
          <span className="text-xs font-normal text-slate-400">
            — capture Phonesuite prospects before the app goes live
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400" data-testid="uc-hospitality-leads-count">
            {loading ? "…" : `${leads.length} lead${leads.length === 1 ? "" : "s"}`}
          </span>
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="uc-hospitality-leads-view-toggle">
            <button type="button" onClick={() => setViewPersist("table")}
                    data-testid="uc-hospitality-leads-view-table"
                    title="Table view"
                    className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                      view === "table" ? "bg-primary text-primary-foreground" : "text-slate-500 hover:text-slate-700"
                    }`}>
              <List className="h-3.5 w-3.5" /> Table
            </button>
            <button type="button" onClick={() => setViewPersist("kanban")}
                    data-testid="uc-hospitality-leads-view-kanban"
                    title="Kanban view"
                    className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                      view === "kanban" ? "bg-primary text-primary-foreground" : "text-slate-500 hover:text-slate-700"
                    }`}>
              <LayoutGrid className="h-3.5 w-3.5" /> Kanban
            </button>
          </div>
          <Button size="sm" variant="outline" onClick={() => exportLeadsCsv(leads)}
                  disabled={!leads.length}
                  data-testid="uc-hospitality-lead-export">
            <FileDown className="h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}
                  data-testid="uc-hospitality-lead-import-open">
            <Upload className="h-3.5 w-3.5" /> Import CSV
          </Button>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="uc-hospitality-lead-add">
            <Plus className="h-3.5 w-3.5" /> New Lead
          </Button>
        </div>
      </div>

      <PipelineKPI leads={leads} />

      {leads.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
             data-testid="uc-hospitality-leads-empty">
          No prospects captured yet. Add the first Phonesuite hospitality lead to kick off the pipeline.
        </div>
      ) : view === "kanban" ? (
        <LeadKanban leads={leads} onOpen={setDrawerId} onMove={moveStage} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm" data-testid="uc-hospitality-leads-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Property</th>
                <th className="px-3 py-2 text-left font-medium">Contact</th>
                <th className="px-3 py-2 text-left font-medium">Rooms</th>
                <th className="px-3 py-2 text-left font-medium">City</th>
                <th className="px-3 py-2 text-left font-medium">Stage</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((l) => {
                const s = stageBadge(l.stage);
                return (
                  <tr key={l.lead_id} onClick={() => setDrawerId(l.lead_id)}
                      className="cursor-pointer text-slate-700 hover:bg-slate-50"
                      data-testid={`uc-hospitality-lead-row-${l.lead_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{l.property_name}</div>
                      {l.brand && <div className="text-[11px] text-slate-400">{l.brand}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div>{l.contact_name}</div>
                      <div className="text-[11px] text-slate-400">{l.contact_email}</div>
                    </td>
                    <td className="px-3 py-2 tabular-nums">{l.room_count ?? "—"}</td>
                    <td className="px-3 py-2">
                      {l.city ? `${l.city}${l.province_state ? `, ${l.province_state}` : ""}` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.tone}`}>{s.label}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{l.source || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="uc-hospitality-lead-dialog">
          <DialogHeader>
            <DialogTitle>New Hospitality Lead</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="text-xs">Property name *</Label>
              <Input value={form.property_name} onChange={(e) => set({ property_name: e.target.value })}
                     data-testid="uc-hospitality-lead-property" placeholder="Silver Birch Inn" />
            </div>
            <div>
              <Label className="text-xs">Brand</Label>
              <Input value={form.brand} onChange={(e) => set({ brand: e.target.value })}
                     data-testid="uc-hospitality-lead-brand" placeholder="Marriott, Hilton, Independent…" />
            </div>
            <div>
              <Label className="text-xs">Room count</Label>
              <Input type="number" min="0" value={form.room_count}
                     onChange={(e) => set({ room_count: e.target.value })}
                     data-testid="uc-hospitality-lead-rooms" placeholder="48" />
            </div>
            <div>
              <Label className="text-xs">Est. MRR (CAD) <span className="text-slate-300">— overrides rooms est.</span></Label>
              <Input type="number" min="0" step="0.01" value={form.est_mrr_cad}
                     onChange={(e) => set({ est_mrr_cad: e.target.value })}
                     data-testid="uc-hospitality-lead-mrr" placeholder="1150" />
            </div>
            <div>
              <Label className="text-xs">Contact name *</Label>
              <Input value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })}
                     data-testid="uc-hospitality-lead-contact-name" placeholder="Nadia Cross" />
            </div>
            <div>
              <Label className="text-xs">Contact email *</Label>
              <Input type="email" value={form.contact_email}
                     onChange={(e) => set({ contact_email: e.target.value })}
                     data-testid="uc-hospitality-lead-contact-email" placeholder="nadia@silverbirch.example" />
            </div>
            <div>
              <Label className="text-xs">Contact phone</Label>
              <Input value={form.contact_phone} onChange={(e) => set({ contact_phone: e.target.value })}
                     data-testid="uc-hospitality-lead-contact-phone" placeholder="+1 587 555 0142" />
            </div>
            <div>
              <Label className="text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => set({ stage: v })}>
                <SelectTrigger data-testid="uc-hospitality-lead-stage">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input value={form.city} onChange={(e) => set({ city: e.target.value })}
                     data-testid="uc-hospitality-lead-city" placeholder="Kelowna" />
            </div>
            <div>
              <Label className="text-xs">Province / State</Label>
              <Input value={form.province_state} onChange={(e) => set({ province_state: e.target.value })}
                     data-testid="uc-hospitality-lead-region" placeholder="BC" />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Input value={form.source} onChange={(e) => set({ source: e.target.value })}
                     data-testid="uc-hospitality-lead-source" placeholder="Phonesuite referral, cold, web…" />
            </div>
            <div>
              <Label className="text-xs">Phonesuite PBX model</Label>
              <Input value={form.phonesuite_pbx_model}
                     onChange={(e) => set({ phonesuite_pbx_model: e.target.value })}
                     data-testid="uc-hospitality-lead-pbx" placeholder="VS 1024" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })}
                        data-testid="uc-hospitality-lead-notes"
                        placeholder="Anything relevant — trunk requirements, competing carriers, timeline…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}
                    data-testid="uc-hospitality-lead-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="uc-hospitality-lead-save">
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Capture lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeadDrawer leadId={drawerId} onClose={() => setDrawerId(null)}
                  onChanged={(patched) => {
                    if (patched) {
                      setLeads((rows) => rows.map((r) => (r.lead_id === patched.lead_id ? patched : r)));
                    } else {
                      load();
                    }
                  }} />

      <BulkImportDialog open={importOpen} onClose={() => setImportOpen(false)}
                        onImported={load} />
    </Card>
  );
}


// ---------------------- Kanban view ----------------------
// HTML5 drag-and-drop board grouped by stage. Column headers show a badge
// tinted to match the stage. Cards render minimal contact info so the board
// stays scannable; click opens the drawer for the full view.

function LeadKanban({ leads, onOpen, onMove }) {
  const [hover, setHover] = useState(null);
  const columns = STAGES.map((s) => ({
    ...s,
    rows: leads.filter((l) => l.stage === s.value),
  }));

  const onDragStart = (e, leadId) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", leadId);
  };
  const onDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setHover(stage);
  };
  const onDrop = (e, stage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain");
    setHover(null);
    if (leadId) onMove(leadId, stage);
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5"
         data-testid="uc-hospitality-leads-kanban">
      {columns.map((col) => (
        <div key={col.value}
             onDragOver={(e) => onDragOver(e, col.value)}
             onDragLeave={() => setHover((h) => (h === col.value ? null : h))}
             onDrop={(e) => onDrop(e, col.value)}
             data-testid={`uc-hospitality-kanban-col-${col.value}`}
             className={`flex min-h-[180px] flex-col rounded-lg border transition-colors ${
               hover === col.value ? "border-primary bg-primary/5" : "border-slate-200 bg-slate-50/60"
             }`}>
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${col.tone}`}>
              {col.label}
            </span>
            <span className="text-[11px] font-medium text-slate-400">{col.rows.length}</span>
          </div>
          <div className="flex-1 space-y-2 p-2">
            {col.rows.map((l) => {
              const annual = leadAnnualCAD(l);
              const weighted = annual * (STAGE_WEIGHTS[l.stage] ?? 0);
              const cadShort = (n) => n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
              const isOverride = leadUsesOverride(l);
              return (
                <div key={l.lead_id}
                     draggable
                     onDragStart={(e) => onDragStart(e, l.lead_id)}
                     onClick={() => onOpen(l.lead_id)}
                     data-testid={`uc-hospitality-kanban-card-${l.lead_id}`}
                     className="cursor-grab rounded-md border border-slate-200 bg-white p-2 shadow-sm transition-shadow hover:shadow active:cursor-grabbing">
                  <div className="flex items-start gap-1.5">
                    <Building2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-slate-800">{l.property_name}</div>
                      <div className="truncate text-[11px] text-slate-500">{l.contact_name}</div>
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-400">
                    {l.city && <span>{l.city}{l.province_state ? `, ${l.province_state}` : ""}</span>}
                    {l.room_count != null && <span>· {l.room_count} rms</span>}
                  </div>
                  {annual > 0 && (
                    <div className="mt-1.5 flex items-center justify-between rounded bg-slate-50 px-1.5 py-0.5 text-[10px]"
                         data-testid={`uc-hospitality-kanban-card-value-${l.lead_id}`}
                         title={`Annual ${cadShort(annual)}${isOverride ? " (from quote)" : " (rooms est.)"} · weight ${(STAGE_WEIGHTS[l.stage] ?? 0) * 100}%`}>
                      <span className="text-slate-500">
                        {cadShort(annual)}
                        {isOverride && <span className="ml-1 text-primary/70">·q</span>}
                      </span>
                      <span className="font-semibold text-emerald-600">{cadShort(weighted)}</span>
                    </div>
                  )}
                </div>
              );
            })}
            {col.rows.length === 0 && (
              <div className="rounded border border-dashed border-slate-200 py-4 text-center text-[11px] text-slate-400">
                Drop leads here
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


// ---------------------- Lead detail drawer ----------------------
// Right-side Sheet showing full contact info, activity timeline, quick stage
// buttons ("Advance stage" jumps to the next non-terminal step) and a small
// form to log a new touch. Optimistic UI on stage moves.

const ACTIVITY_ICONS = {
  note:         StickyNote,
  call:         Phone,
  email:        Mail,
  meeting:      CalendarClock,
  stage_change: MoveRight,
  created:      Sparkles,
};

const ACTIVITY_KINDS = [
  { value: "note",    label: "Note"    },
  { value: "call",    label: "Call"    },
  { value: "email",   label: "Email"   },
  { value: "meeting", label: "Meeting" },
];

function LeadDrawer({ leadId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activityForm, setActivityForm] = useState({ kind: "note", subject: "", body: "" });

  useEffect(() => {
    if (!leadId) { setDetail(null); return; }
    setLoading(true);
    api.get(`/hospitality/leads/${leadId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (!leadId) return <Sheet open={false} onOpenChange={onClose}><SheetContent /></Sheet>;
  const lead = detail?.lead;
  const activities = detail?.activities || [];
  const currentIdx = lead ? STAGES.findIndex((s) => s.value === lead.stage) : -1;
  const nextStage = currentIdx >= 0 && currentIdx < 3 ? STAGES[currentIdx + 1] : null;

  const moveTo = async (stage) => {
    if (!lead || lead.stage === stage) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/hospitality/leads/${lead.lead_id}`, { stage });
      setDetail((d) => ({ ...d, lead: data }));
      onChanged(data);
      // refresh activities so the auto-logged stage_change entry appears
      const { data: fresh } = await api.get(`/hospitality/leads/${lead.lead_id}`);
      setDetail(fresh);
      toast.success(`${lead.property_name} → ${stageBadge(stage).label}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const logActivity = async () => {
    if (!activityForm.body.trim()) { toast.error("Body is required"); return; }
    setBusy(true);
    try {
      await api.post(`/hospitality/leads/${lead.lead_id}/activities`, {
        kind: activityForm.kind,
        subject: activityForm.subject.trim() || null,
        body: activityForm.body.trim(),
      });
      const { data } = await api.get(`/hospitality/leads/${lead.lead_id}`);
      setDetail(data);
      setActivityForm({ kind: "note", subject: "", body: "" });
      toast.success("Activity logged");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Log failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!leadId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="uc-hospitality-lead-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            {lead?.property_name || (loading ? "Loading…" : "Lead")}
          </SheetTitle>
        </SheetHeader>

        {lead && (
          <div className="mt-4 space-y-5">
            {/* Stage + quick actions */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Stage</div>
                  <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${stageBadge(lead.stage).tone}`}
                        data-testid="uc-hospitality-drawer-stage">
                    {stageBadge(lead.stage).label}
                  </span>
                </div>
                {nextStage && (
                  <Button size="sm" onClick={() => moveTo(nextStage.value)} disabled={busy}
                          data-testid="uc-hospitality-drawer-advance">
                    <ArrowRight className="h-3.5 w-3.5" /> Advance to {nextStage.label}
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.filter((s) => s.value !== lead.stage).map((s) => (
                  <button key={s.value} type="button" disabled={busy}
                          onClick={() => moveTo(s.value)}
                          data-testid={`uc-hospitality-drawer-move-${s.value}`}
                          className={`rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50 ${s.tone.replace("bg-", "hover:bg-")}`}>
                    Move to {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact block */}
            <div className="rounded-lg border border-slate-200 p-3 text-sm">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Contact</div>
              <div className="space-y-1 text-slate-700">
                <div className="font-medium">{lead.contact_name}</div>
                {lead.contact_email && (
                  <a href={`mailto:${lead.contact_email}`} className="flex items-center gap-1.5 text-primary hover:underline"
                     data-testid="uc-hospitality-drawer-email">
                    <Mail className="h-3.5 w-3.5" /> {lead.contact_email}
                  </a>
                )}
                {lead.contact_phone && (
                  <a href={`tel:${lead.contact_phone}`} className="flex items-center gap-1.5 text-slate-600 hover:underline"
                     data-testid="uc-hospitality-drawer-phone">
                    <Phone className="h-3.5 w-3.5" /> {lead.contact_phone}
                  </a>
                )}
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                {[
                  ["Brand",           lead.brand],
                  ["Rooms",           lead.room_count],
                  ["Est. MRR",        lead.est_mrr_cad ? `$${Number(lead.est_mrr_cad).toLocaleString("en-CA")}/mo (quote)` : null],
                  ["City",            lead.city && `${lead.city}${lead.province_state ? ", " + lead.province_state : ""}`],
                  ["Country",         lead.country],
                  ["Source",          lead.source],
                  ["Phonesuite PBX",  lead.phonesuite_pbx_model],
                  ["Assigned to",     lead.assigned_to_name],
                  ["Created",         lead.created_at ? new Date(lead.created_at).toLocaleDateString() : null],
                ].filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 border-b border-slate-100 py-0.5">
                    <dt className="text-slate-400">{k}</dt>
                    <dd className="truncate text-right font-medium text-slate-700">{v}</dd>
                  </div>
                ))}
              </dl>
              {lead.notes && (
                <div className="mt-3 rounded bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">
                  {lead.notes}
                </div>
              )}
            </div>

            {/* Log activity form */}
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Log activity</div>
                <Select value={activityForm.kind}
                        onValueChange={(v) => setActivityForm((f) => ({ ...f, kind: v }))}>
                  <SelectTrigger className="h-7 w-28 text-xs" data-testid="uc-hospitality-drawer-activity-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Subject (optional)" value={activityForm.subject}
                     onChange={(e) => setActivityForm((f) => ({ ...f, subject: e.target.value }))}
                     data-testid="uc-hospitality-drawer-activity-subject"
                     className="mb-2 h-8 text-sm" />
              <Textarea rows={3} placeholder="What happened?" value={activityForm.body}
                        onChange={(e) => setActivityForm((f) => ({ ...f, body: e.target.value }))}
                        data-testid="uc-hospitality-drawer-activity-body"
                        className="text-sm" />
              <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={logActivity} disabled={busy || !activityForm.body.trim()}
                        data-testid="uc-hospitality-drawer-activity-save">
                  <Save className="h-3.5 w-3.5" /> Log
                </Button>
              </div>
            </div>

            {/* Iter 91ag — Deal attachments (drop PDF → Claude extracts MRR) */}
            <DealAttachments leadId={lead.lead_id}
                             currentMrr={lead.est_mrr_cad}
                             onApplied={(fresh) => {
                               setDetail((d) => ({ ...d, lead: fresh }));
                               onChanged(fresh);
                               // pull fresh activities so the "Applied MRR" note appears
                               api.get(`/hospitality/leads/${fresh.lead_id}`).then(({ data }) => setDetail(data));
                             }} />

            {/* Timeline */}
            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Timeline ({activities.length})
              </div>
              {activities.length === 0 ? (
                <div className="rounded border border-dashed border-slate-200 py-4 text-center text-xs text-slate-400"
                     data-testid="uc-hospitality-drawer-timeline-empty">
                  No touches yet. Log the first call, email or note above.
                </div>
              ) : (
                <ol className="space-y-2" data-testid="uc-hospitality-drawer-timeline">
                  {activities.map((a) => {
                    const Icon = ACTIVITY_ICONS[a.kind] || StickyNote;
                    return (
                      <li key={a.activity_id} className="flex gap-2 rounded-md border border-slate-100 bg-white p-2">
                        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="truncate text-xs font-medium text-slate-800">
                              {a.subject || (a.body ? a.body.slice(0, 80) : ACTIVITY_KINDS.find((k) => k.value === a.kind)?.label || a.kind)}
                            </div>
                            <div className="flex-shrink-0 text-[10px] text-slate-400">
                              {new Date(a.created_at).toLocaleString()}
                            </div>
                          </div>
                          {a.body && a.subject && <div className="mt-0.5 whitespace-pre-line text-[11px] text-slate-600">{a.body}</div>}
                          {a.by_name && <div className="mt-1 text-[10px] text-slate-400">by {a.by_name}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


export default function UCHospitality() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin" || user?.base_role === "super_admin";
  return (
    <div className="space-y-4" data-testid="uc-hospitality-page">
      <PageHeader
        icon={<Hotel className="h-5 w-5 text-primary" />}
        title="UC Hospitality"
        subtitle="Phonesuite partnership — specialized workflows for hospitality customers."
      />

      <Card className="p-8">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Hotel className="h-6 w-6 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            <div className="text-lg font-semibold text-slate-800">
              UC Hospitality is on the roadmap
            </div>
            <Badge variant="outline" data-testid="uc-hospitality-status-badge"
                   className="border-amber-300 bg-amber-50 text-amber-700">
              Not Active
            </Badge>
          </div>
          <div className="mt-1 max-w-xl text-sm text-slate-500">
            Keith will drop the framework and integration notes next week.
            Julian focuses on the Conduit 2 MVP first, then owns the
            Hospitality build in a planned future re-deploy.
          </div>
        </div>

        <div className="mx-auto mt-6 max-w-2xl">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <Sparkles className="h-3.5 w-3.5" /> Planned scope
          </div>
          <ul className="space-y-1.5 text-sm text-slate-600" data-testid="uc-hospitality-scope-list">
            {PLANNED.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/60" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <FrameworkNotes isSuperAdmin={isSuperAdmin} />
      <LeadsPanel />
      <LedgerPreview isSuperAdmin={isSuperAdmin} />
      <SchemaPreview />
    </div>
  );
}


// ---------------------- Pipeline KPI ----------------------
// Iter 91ad — weighted forecast card above the pipeline. Value proxy is
// `est_annual_cad = room_count × ARPU × 12` since leads don't yet carry an
// explicit dollar field. Stage probabilities picked to match Keith's ask:
// Qualified × 30% · Quote sent × 60% · Won × 100% (and New × 10%, Lost × 0%).

const ARPU_MONTHLY_CAD = 30;   // per Phonesuite room, monthly recurring
const STAGE_WEIGHTS = {
  new:         0.10,
  qualified:   0.30,
  quote_sent:  0.60,
  won:         1.00,
  lost:        0.00,
};
// Iter 91ae — honour explicit `est_mrr_cad` (from a real quote) over the
// room-based estimate; fall back to rooms × ARPU × 12 when unset.
const leadAnnualCAD = (l) => {
  const mrr = Number(l?.est_mrr_cad);
  if (Number.isFinite(mrr) && mrr > 0) return mrr * 12;
  return Math.max(0, (l?.room_count || 0)) * ARPU_MONTHLY_CAD * 12;
};
const leadUsesOverride = (l) => Number.isFinite(Number(l?.est_mrr_cad)) && Number(l.est_mrr_cad) > 0;

function PipelineKPI({ leads }) {
  const stats = useMemo(() => {
    const buckets = {};
    STAGES.forEach((s) => {
      buckets[s.value] = { count: 0, value: 0, weighted: 0, weight: STAGE_WEIGHTS[s.value] ?? 0 };
    });
    let total = 0, weighted = 0;
    (leads || []).forEach((l) => {
      const v = leadAnnualCAD(l);
      const b = buckets[l.stage] || buckets.new;
      b.count += 1;
      b.value += v;
      b.weighted += v * (STAGE_WEIGHTS[l.stage] ?? 0);
      total += v;
      weighted += v * (STAGE_WEIGHTS[l.stage] ?? 0);
    });
    return { buckets, total, weighted };
  }, [leads]);

  const cad = (n) => `$${Math.round(n).toLocaleString("en-CA")}`;

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3"
         data-testid="uc-hospitality-pipeline-kpi">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <TrendingUp className="h-3.5 w-3.5 text-primary" /> Pipeline Value
          <span className="text-[10px] font-normal text-slate-400">
            — estimate = rooms × ${ARPU_MONTHLY_CAD}/room/mo × 12
          </span>
        </div>
        <div className="flex items-baseline gap-3 text-xs">
          <div className="flex items-baseline gap-1">
            <span className="text-slate-400">Total</span>
            <span className="text-base font-semibold tabular-nums text-slate-800"
                  data-testid="uc-hospitality-pipeline-total">{cad(stats.total)}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-slate-400">Weighted</span>
            <span className="text-base font-semibold tabular-nums text-emerald-600"
                  data-testid="uc-hospitality-pipeline-weighted">{cad(stats.weighted)}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {STAGES.map((s) => {
          const b = stats.buckets[s.value];
          const wpct = Math.round(b.weight * 100);
          return (
            <div key={s.value}
                 data-testid={`uc-hospitality-pipeline-stage-${s.value}`}
                 className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-semibold ${s.tone}`}>
                  {s.label}
                </span>
                <span className="text-[10px] font-medium text-slate-400">{b.count}</span>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-1">
                <span className="text-[10px] text-slate-400">×{wpct}%</span>
                <span className="text-xs font-semibold tabular-nums text-slate-800">{cad(b.weighted)}</span>
              </div>
              {b.value > 0 && b.value !== b.weighted && (
                <div className="text-right text-[10px] text-slate-400">of {cad(b.value)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------- Bulk CSV Import ----------------------
// Iter 91ad — drag-and-drop CSV import for the Prospect Pipeline. Parses the
// CSV in the browser, previews the first rows, posts the batch to
// /hospitality/leads/bulk and renders per-row success/error inline.

const IMPORT_TEMPLATE_COLS = [
  "property_name", "brand", "room_count", "contact_name", "contact_email",
  "contact_phone", "city", "province_state", "country", "stage", "source",
  "phonesuite_pbx_model", "est_mrr_cad", "notes",
];

// Iter 91ae — round-trip: export the current leads list in the same shape
// as the import template so admins can bulk-edit in Excel and re-upload.
// Any value containing `,` `"` or newline gets RFC-4180 quoted; embedded
// double-quotes are escaped by doubling.  Also prefixes any cell starting
// with `= + - @` with a leading apostrophe to defuse CSV formula injection
// (same posture as the Broken Reasons + Job Board exports).
function csvCell(v) {
  if (v == null) return "";
  let s = String(v);
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  if (/[",\n\r]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}
function exportLeadsCsv(leads) {
  const rows = [
    IMPORT_TEMPLATE_COLS.join(","),
    ...leads.map((l) => IMPORT_TEMPLATE_COLS.map((c) => csvCell(l[c])).join(",")),
  ];
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `hospitality-leads-${stamp}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`Exported ${leads.length} lead${leads.length === 1 ? "" : "s"}`);
}

const IMPORT_TEMPLATE_SAMPLE = [
  "Silver Birch Inn,Independent,48,Nadia Cross,nadia@silverbirch.example,+15875550142,Kelowna,BC,CA,qualified,Phonesuite referral,VS 1024,1150,",
  "Cascade Falls Resort,Independent,94,Julia Bristow,julia@cascadefalls.example,,Banff,AB,CA,new,,,,\"Needs failover trunking\"",
];

function parseCsv(text) {
  // Small dependency-free CSV parser that handles quoted commas + escaped
  // double-quotes. Enough for our importer needs.
  const rows = [];
  let cell = "", row = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], n = text[i + 1];
    if (inQ) {
      if (c === '"' && n === '"') { cell += '"'; i++; }
      else if (c === '"')          { inQ = false; }
      else                          { cell += c; }
    } else {
      if (c === '"')               { inQ = true; }
      else if (c === ",")          { row.push(cell); cell = ""; }
      else if (c === "\r")         { /* skip */ }
      else if (c === "\n")         { row.push(cell); rows.push(row); cell = ""; row = []; }
      else                          { cell += c; }
    }
  }
  if (cell !== "" || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((v) => (v || "").trim() !== ""));
}

function BulkImportDialog({ open, onClose, onImported }) {
  const [rows, setRows] = useState([]);         // parsed rows (excl. header)
  const [headers, setHeaders] = useState([]);   // parsed header row
  const [results, setResults] = useState(null); // POST response
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setRows([]); setHeaders([]); setResults(null); setBusy(false); };
  const handleClose = () => { reset(); onClose(); };

  const ingest = (text) => {
    const parsed = parseCsv(text);
    if (parsed.length < 2) { toast.error("CSV needs a header row plus at least one data row"); return; }
    const hdr = parsed[0].map((h) => h.trim().toLowerCase());
    const body = parsed.slice(1);
    setHeaders(hdr);
    setRows(body);
    setResults(null);
  };

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("CSV must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload  = () => ingest(String(reader.result || ""));
    reader.onerror = () => toast.error("Read failed");
    reader.readAsText(file);
  };

  const rowsAsObjects = useMemo(() => {
    if (!headers.length) return [];
    return rows.map((r) => {
      const obj = {};
      headers.forEach((h, i) => {
        const val = (r[i] ?? "").trim();
        if (val === "") return;
        if (h === "room_count") {
          const n = parseInt(val, 10);
          if (!Number.isNaN(n)) obj[h] = n;
        } else if (h === "est_mrr_cad") {
          const n = parseFloat(val);
          if (!Number.isNaN(n)) obj[h] = n;
        } else {
          obj[h] = val;
        }
      });
      return obj;
    });
  }, [headers, rows]);

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hospitality/leads/bulk", { rows: rowsAsObjects });
      setResults(data);
      if (data.ok > 0) toast.success(`Imported ${data.ok} of ${data.total} leads`);
      if (data.ok !== data.total) toast.error(`${data.total - data.ok} row(s) failed — see details below`);
      onImported();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = [IMPORT_TEMPLATE_COLS.join(","), ...IMPORT_TEMPLATE_SAMPLE].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "hospitality-leads-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="uc-hospitality-lead-import-dialog">
        <DialogHeader>
          <DialogTitle>Import Hospitality Leads (CSV)</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {!rows.length ? (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
                data-testid="uc-hospitality-lead-import-drop"
                className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  dragOver ? "border-primary bg-primary/5" : "border-slate-300 bg-slate-50/50"
                }`}
              >
                <FileSpreadsheet className="h-8 w-8 text-slate-400" />
                <div className="text-sm font-medium text-slate-700">
                  Drop CSV here, or <label className="cursor-pointer text-primary underline"
                    data-testid="uc-hospitality-lead-import-picker">
                    browse
                    <input type="file" accept=".csv,text/csv" className="hidden"
                           onChange={(e) => onFile(e.target.files?.[0])} />
                  </label>
                </div>
                <div className="text-xs text-slate-500">
                  Up to 1,000 rows · 2 MB max · headers required
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs">
                <div className="text-slate-500">
                  Expected columns: <code className="rounded bg-white px-1 py-0.5 text-[10px] text-slate-600">property_name, contact_name, contact_email</code> required; the rest optional.
                </div>
                <Button size="sm" variant="ghost" onClick={downloadTemplate}
                        data-testid="uc-hospitality-lead-import-template">
                  <FileDown className="h-3.5 w-3.5" /> Template
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <div><span className="font-semibold text-slate-700">{rows.length}</span> row{rows.length === 1 ? "" : "s"} parsed · {headers.length} column{headers.length === 1 ? "" : "s"}</div>
                <Button size="sm" variant="ghost" onClick={reset} disabled={busy}
                        data-testid="uc-hospitality-lead-import-clear">Clear</Button>
              </div>
              <div className="max-h-52 overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs" data-testid="uc-hospitality-lead-import-preview">
                  <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-1 text-left">#</th>
                      {headers.map((h) => <th key={h} className="px-2 py-1 text-left font-medium">{h}</th>)}
                      {results && <th className="px-2 py-1 text-left font-medium">Result</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.slice(0, 20).map((r, i) => {
                      const res = results?.results?.[i];
                      return (
                        <tr key={i} className={res?.ok === false ? "bg-rose-50" : res?.ok ? "bg-emerald-50/60" : ""}>
                          <td className="px-2 py-1 text-slate-400">{i + 1}</td>
                          {headers.map((h, j) => (
                            <td key={h} className="px-2 py-1 text-slate-700"><span className="line-clamp-1">{r[j] || ""}</span></td>
                          ))}
                          {results && (
                            <td className="px-2 py-1">
                              {res?.ok
                                ? <span className="rounded-full bg-emerald-100 px-1.5 py-0 text-[10px] font-medium text-emerald-700">imported</span>
                                : <span className="text-[10px] text-rose-700" title={res?.error}>{(res?.error || "").slice(0, 60)}</span>}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > 20 && (
                  <div className="border-t border-slate-100 bg-slate-50 px-2 py-1 text-center text-[10px] text-slate-500">
                    Showing 20 of {rows.length} rows
                  </div>
                )}
              </div>
              {results && (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700"
                     data-testid="uc-hospitality-lead-import-summary">
                  <span className="font-semibold text-emerald-700">{results.ok} imported</span>
                  {results.total - results.ok > 0 && (
                    <span className="ml-3 font-semibold text-rose-700">{results.total - results.ok} failed</span>
                  )}
                  <span className="ml-3 text-slate-400">of {results.total} total</span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={busy}
                  data-testid="uc-hospitality-lead-import-cancel">
            {results ? "Close" : "Cancel"}
          </Button>
          {rows.length > 0 && !results && (
            <Button onClick={doImport} disabled={busy || rows.length === 0}
                    data-testid="uc-hospitality-lead-import-run">
              <Upload className="h-3.5 w-3.5" /> {busy ? "Importing…" : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ---------------------- Deal Attachments (PDF → Claude → est_mrr_cad) ----------------------
// Iter 91ag — drop a signed quote / MSA PDF onto a lead, Claude extracts the
// monthly recurring dollar amount, Julian confirms in a modal, then it lands
// on `est_mrr_cad`. Two-step so the AI extraction is always human-approved.

function DealAttachments({ leadId, currentMrr, onApplied }) {
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null); // { attachment_id, filename, extracted, llm_error, ... }
  const [applying, setApplying] = useState(false);

  const load = () => {
    api.get(`/hospitality/leads/${leadId}/attachments`)
      .then(({ data }) => setAttachments(data.attachments || []))
      .catch(() => setAttachments([]));
  };
  useEffect(() => { load(); }, [leadId]);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      toast.error("PDF only");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("PDF must be under 8 MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file, file.name);
      const { data } = await api.post(`/hospitality/leads/${leadId}/attachments`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      load();
      const mrr = data?.extracted?.mrr_cad;
      if (mrr != null)      toast.success(`Extracted $${Math.round(mrr).toLocaleString()}/mo — review to apply`);
      else if (data?.llm_error) toast.error("AI couldn't extract an MRR — see details in dialog");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const applyMrr = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(
        `/hospitality/leads/${leadId}/attachments/${preview.attachment_id}/apply-mrr`,
      );
      toast.success(`est_mrr_cad set to $${Math.round(data.est_mrr_cad).toLocaleString()}/mo`);
      onApplied(data.lead);
      setPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const ex = preview?.extracted || {};
  const usdBlocked = ex.currency === "USD";
  const canApply = !usdBlocked && Number.isFinite(Number(ex.mrr_cad)) && Number(ex.mrr_cad) > 0;
  const confMap = { high: "text-emerald-700 bg-emerald-100", medium: "text-amber-700 bg-amber-100", low: "text-rose-700 bg-rose-100" };
  const confTone = confMap[ex.confidence] || "text-slate-700 bg-slate-100";

  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="uc-hospitality-drawer-attachments">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          <Paperclip className="h-3.5 w-3.5" />
          Deal attachments ({attachments.length})
        </div>
        <label className={`inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium ${busy ? "cursor-wait opacity-60" : "hover:border-primary hover:text-primary"}`}
               data-testid="uc-hospitality-attachment-upload">
          <Upload className="h-3 w-3" /> Upload PDF
          <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={busy}
                 onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
      </div>

      {/* Drop zone (compact when there are existing attachments) */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
        data-testid="uc-hospitality-attachment-drop"
        className={`rounded-md border-2 border-dashed p-3 text-center text-[11px] transition-colors ${
          dragOver ? "border-primary bg-primary/5 text-primary"
                   : busy ? "border-slate-200 bg-slate-50 text-slate-400" : "border-slate-200 bg-slate-50/50 text-slate-500"
        }`}
      >
        {busy ? "Extracting with Claude…" : "Drop a signed quote / MSA PDF here — Claude will extract the MRR."}
      </div>

      {/* Existing attachments */}
      {attachments.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="uc-hospitality-attachment-list">
          {attachments.map((a) => (
            <li key={a.attachment_id}
                data-testid={`uc-hospitality-attachment-row-${a.attachment_id}`}
                className="flex items-center gap-2 rounded-md border border-slate-100 bg-white p-1.5 text-[11px]">
              <FileText className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-slate-700">{a.filename}</div>
                <div className="text-[10px] text-slate-400">
                  {(a.size_bytes / 1024).toFixed(0)} KB · {a.page_count} pg · {new Date(a.uploaded_at).toLocaleDateString()}
                </div>
              </div>
              {a.extracted?.mrr_cad != null && (
                <span className="rounded-full bg-emerald-100 px-1.5 py-0 text-[10px] font-semibold text-emerald-700"
                      title={a.text_snippet}>
                  ${Math.round(a.extracted.mrr_cad).toLocaleString()}/mo
                </span>
              )}
              {a.applied && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600" title="Applied to est_mrr_cad" />}
              <a href={`${API_BASE}/hospitality/leads/${leadId}/attachments/${a.attachment_id}/download`}
                 target="_blank" rel="noreferrer"
                 data-testid={`uc-hospitality-attachment-download-${a.attachment_id}`}
                 className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                 title="Download">
                <FileDown className="h-3.5 w-3.5" />
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* Confirm modal — shows the extracted MRR + snippet before writing to lead */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent className="max-w-md" data-testid="uc-hospitality-attachment-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-primary" />
              Review extracted MRR
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-500">
                <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom" />
                {preview.filename} · {(preview.size_bytes / 1024).toFixed(0)} KB · {preview.page_count} pg
              </div>

              {ex.mrr_cad != null ? (
                <>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"
                       data-testid="uc-hospitality-attachment-extracted">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Extracted MRR</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold tabular-nums text-emerald-800">
                        ${Number(ex.mrr_cad).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-xs text-emerald-700">{ex.currency}/mo</span>
                      <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${confTone}`}
                            data-testid="uc-hospitality-attachment-confidence">
                        {ex.confidence || "?"}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                      {ex.one_time_cad != null && (
                        <div><span className="text-slate-400">One-time</span> <span className="ml-1 font-medium">${Number(ex.one_time_cad).toLocaleString()}</span></div>
                      )}
                      {ex.term_months != null && (
                        <div><span className="text-slate-400">Term</span> <span className="ml-1 font-medium">{ex.term_months} mo</span></div>
                      )}
                    </div>
                  </div>

                  {ex.snippet && (
                    <div>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Source snippet</div>
                      <blockquote className="whitespace-pre-line rounded-md border-l-2 border-primary/40 bg-slate-50 p-2 text-[11px] italic text-slate-600"
                                  data-testid="uc-hospitality-attachment-snippet">
                        {ex.snippet}
                      </blockquote>
                    </div>
                  )}

                  {ex.reasoning && (
                    <div className="text-[11px] text-slate-500">
                      <span className="font-semibold text-slate-600">Claude:</span> {ex.reasoning}
                    </div>
                  )}

                  {usdBlocked && (
                    <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                      Quote is in USD. I won&apos;t auto-convert — enter the CAD equivalent manually in the Detail drawer&apos;s Est. MRR field.
                    </div>
                  )}

                  {currentMrr != null && Number(currentMrr) > 0 && (
                    <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-500">
                      Current <code className="rounded bg-white px-1 text-slate-700">est_mrr_cad</code> is <b>${Number(currentMrr).toLocaleString()}/mo</b>.
                      Applying will overwrite it.
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-[11px] text-rose-800"
                     data-testid="uc-hospitality-attachment-noextract">
                  <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Couldn&apos;t extract an MRR value.</div>
                    <div className="mt-0.5">{preview.llm_error === "no_llm_key"
                      ? "The Emergent LLM key isn't set on the backend."
                      : preview.llm_error === "no_extractable_text"
                        ? "PDF text couldn't be read — try a text-based (non-scanned) PDF."
                        : "The document may not contain an explicit MRR line. Enter Est. MRR manually in the drawer."}</div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreview(null)} disabled={applying}
                    data-testid="uc-hospitality-attachment-preview-close">Close</Button>
            {canApply && (
              <Button onClick={applyMrr} disabled={applying}
                      data-testid="uc-hospitality-attachment-apply">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {applying ? "Applying…" : `Apply $${Number(ex.mrr_cad).toLocaleString()}/mo`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

